/**
 * Certificates API
 *
 * API functions for managing contractor certificates
 */

import { buildApiUrl, handleApiResponse } from '@/src/shared/api';
import { fetchWithTokenRefresh } from '@/src/shared/api/refreshToken';

export interface Certificate {
  id: string;
  contractorId: string;
  title: string | null;
  imageUrl: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Get my certificates
 */
export async function getMyCertificates(): Promise<Certificate[]> {
  const url = buildApiUrl('/contractors/me/certificates');

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    credentials: 'include',
  });

  return handleApiResponse<Certificate[]>(response);
}

/**
 * Upload a certificate
 */
export async function uploadCertificate(
  file: File,
  title?: string
): Promise<Certificate> {
  const url = buildApiUrl('/contractors/me/certificates');

  const formData = new FormData();
  formData.append('file', file);
  if (title) {
    formData.append('title', title);
  }

  const response = await fetchWithTokenRefresh(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  return handleApiResponse<Certificate>(response);
}

/**
 * Delete a certificate
 */
export async function deleteCertificate(id: string): Promise<void> {
  const url = buildApiUrl(`/contractors/me/certificates/${id}`);

  const response = await fetchWithTokenRefresh(url, {
    method: 'DELETE',
    credentials: 'include',
  });

  return handleApiResponse<void>(response);
}

/**
 * Get certificates for a contractor by ID (public)
 */
export async function getCertificatesById(
  contractorId: string
): Promise<Certificate[]> {
  const url = buildApiUrl(`/contractors/${contractorId}/certificates`);

  const response = await fetch(url, {
    method: 'GET',
  });

  return handleApiResponse<Certificate[]>(response);
}
